// Действия экрана тренировки: меняют тренировку в IndexedDB и ставят запросы в офлайн-очередь.
// Порядок на сервере: PUT тренировки без окончания → PUT подходов → POST /finish.

import { restTimer } from '@/lib/restTimer'
import {
  getActiveWorkout,
  setActiveWorkout,
  updateActiveWorkout,
  type ActiveExercise,
  type ActiveSet,
  type ActiveWorkout,
} from '@/offline/activeSession'
import { dropMutations, enqueue } from '@/offline/queue'
import { flushSoon } from '@/offline/sync'
import type { EditOperation } from '@/pages/ai/api'
import type { Tracking, WorkoutSet } from '@/pages/workouts/api'

import {
  aiTargets,
  buildExercise,
  nextPosition,
  nextSet,
  pausedSeconds,
  renumberExercises,
  restSeconds,
  sessionPayload,
  sessionUrl,
  setPayload,
  setUrl,
} from './logic'
// Таймер отдыха сообщает серверу конец отдыха — тот шлёт push «Отдых окончен».
import { syncRestAlarm } from './restAlarm'
import { setTimer } from './setTimer'

function mapExercise(workout: ActiveWorkout, key: string, change: (e: ActiveExercise) => ActiveExercise): ActiveWorkout {
  return { ...workout, exercises: workout.exercises.map((e) => (e.key === key ? change(e) : e)) }
}

async function putSet(workout: ActiveWorkout, exercise: ActiveExercise, set: ActiveSet): Promise<void> {
  await enqueue({ method: 'PUT', url: setUrl(workout, set), body: setPayload(exercise, set) })
}

/**
 * Подготовить тренировку: только на устройстве, без запросов на сервер. Её можно смотреть
 * и править (упражнения, подходы), а начинает её «Старт».
 */
export async function prepareWorkout(workout: ActiveWorkout): Promise<void> {
  restTimer.stop()
  cancelSetTimer()
  await setActiveWorkout({ ...workout, draft: true })
}

/** «Старт»: время начала — сейчас, PUT тренировки в очередь. */
export async function startWorkout(now: Date = new Date()): Promise<void> {
  const prepared = getActiveWorkout()
  if (!prepared?.draft) return
  const { draft: _, ...rest } = prepared
  const workout: ActiveWorkout = { ...rest, started_at: now.toISOString() }
  // Сначала в очередь (тренировка должна уйти раньше любого подхода), потом на экран.
  await enqueue({ method: 'PUT', url: sessionUrl(workout), body: sessionPayload(workout) })
  await setActiveWorkout(workout)
  flushSoon()
}

/** Убрать неначатую тренировку: на сервере её нет, спрашивать нечего. */
export async function cancelDraft(): Promise<void> {
  if (getActiveWorkout()?.draft) await setActiveWorkout(null)
}

/** Пауза: время тренировки стоит, отдых и таймер подхода снимаются. */
export async function pauseWorkout(now: Date = new Date()): Promise<void> {
  const workout = getActiveWorkout()
  if (!workout || workout.draft || workout.paused_at) return
  restTimer.stop()
  cancelSetTimer()
  await setActiveWorkout({ ...workout, paused_at: now.toISOString() })
}

/** Продолжить после паузы: простой прибавляется к паузам и в длительность не идёт. */
export async function resumeWorkout(now: Date = new Date()): Promise<void> {
  const workout = getActiveWorkout()
  if (!workout?.paused_at) return
  await setActiveWorkout({ ...workout, paused_at: null, paused_s: pausedSeconds(workout, now.getTime()) })
}

/**
 * Запустить таймер подхода на время. Цель — время, уже стоящее в подходе; на неё ставится
 * push «Время вышло». Идущий отдых на этом кончается.
 */
export function startSetTimer(exercise: ActiveExercise, set: ActiveSet): void {
  void resumeWorkout()
  restTimer.stop()
  setTimer.start(exercise.key, set.id, exercise.name, set.duration_s)
  const at = setTimer.targetAt()
  if (at !== null) void syncRestAlarm(at, exercise.name)
}

/** Сбросить таймер подхода без записи. */
export function cancelSetTimer(): void {
  if (setTimer.stop() !== null) void syncRestAlarm(null)
}

/**
 * Отметить подход выполненным (или снять отметку). Выполнен — сам стартует отдых. Если у
 * подхода шёл таймер, его время по факту (секунд) записывается в подход.
 */
export async function toggleSet(exerciseKey: string, setId: string, now: Date = new Date()): Promise<void> {
  const workout = getActiveWorkout()
  const exercise = workout?.exercises.find((e) => e.key === exerciseKey)
  const set = exercise?.sets.find((s) => s.id === setId)
  // До «Старт» подходы только правятся.
  if (!workout || workout.draft || !exercise || !set) return
  const completed = !set.completed
  // Отметил подход на паузе — значит, тренировка продолжается.
  if (completed && workout.paused_at) {
    await resumeWorkout(now)
    return toggleSet(exerciseKey, setId, now)
  }
  const timed = completed && setTimer.isFor(setId) ? setTimer.stop() : null
  const updated: ActiveSet = {
    ...set,
    ...(timed !== null ? { duration_s: timed } : {}),
    completed,
    completed_at: completed ? now.toISOString() : null,
    saved: true,
  }
  const nextExercise = { ...exercise, sets: exercise.sets.map((s) => (s.id === setId ? updated : s)) }
  await setActiveWorkout(mapExercise(workout, exerciseKey, () => nextExercise))
  await putSet(workout, nextExercise, updated)
  if (completed) restTimer.start(restSeconds(nextExercise, updated))
  flushSoon()
}

type SetPatch = Partial<Pick<ActiveSet, 'weight_kg' | 'reps' | 'duration_s' | 'distance_m' | 'is_warmup'>>

/** Поправить значения подхода; уже отправленный подход уходит на сервер ещё раз. */
export async function changeSet(exerciseKey: string, setId: string, patch: SetPatch): Promise<void> {
  const workout = getActiveWorkout()
  const exercise = workout?.exercises.find((e) => e.key === exerciseKey)
  const set = exercise?.sets.find((s) => s.id === setId)
  if (!workout || !exercise || !set) return
  const updated = { ...set, ...patch }
  const nextExercise = { ...exercise, sets: exercise.sets.map((s) => (s.id === setId ? updated : s)) }
  await setActiveWorkout(mapExercise(workout, exerciseKey, () => nextExercise))
  if (updated.saved) {
    await putSet(workout, nextExercise, updated)
    flushSoon()
  }
}

/** Новый подход — копия предыдущего. Возвращает его id. */
export async function addSet(exerciseKey: string): Promise<string | null> {
  const exercise = getActiveWorkout()?.exercises.find((e) => e.key === exerciseKey)
  if (!exercise) return null
  const set = nextSet(exercise)
  await updateActiveWorkout((w) => mapExercise(w, exerciseKey, (e) => ({ ...e, sets: [...e.sets, set] })))
  return set.id
}

/**
 * Сдвинуть подход на одно место вверх (-1) или вниз (+1). Номера подходов (set_index) идут
 * подряд по новому порядку; уже отправленные подходы с новым номером уходят на сервер ещё раз.
 */
export async function moveSet(exerciseKey: string, setId: string, direction: -1 | 1): Promise<void> {
  const workout = getActiveWorkout()
  const exercise = workout?.exercises.find((e) => e.key === exerciseKey)
  const from = exercise?.sets.findIndex((s) => s.id === setId) ?? -1
  const to = from + direction
  if (!workout || !exercise || from < 0 || to < 0 || to >= exercise.sets.length) return
  const order = [...exercise.sets]
  ;[order[from], order[to]] = [order[to]!, order[from]!]
  const sets = order.map((s, i) => (s.set_index === i ? s : { ...s, set_index: i }))
  const nextExercise = { ...exercise, sets }
  await setActiveWorkout(mapExercise(workout, exerciseKey, () => nextExercise))
  const renumbered = sets.filter((s, i) => s.saved && s !== order[i])
  for (const set of renumbered) await putSet(workout, nextExercise, set)
  if (renumbered.length) flushSoon()
}

export async function removeSet(exerciseKey: string, setId: string): Promise<void> {
  const workout = getActiveWorkout()
  const set = workout?.exercises.find((e) => e.key === exerciseKey)?.sets.find((s) => s.id === setId)
  if (!workout || !set) return
  if (setTimer.isFor(setId)) cancelSetTimer()
  await setActiveWorkout(mapExercise(workout, exerciseKey, (e) => ({ ...e, sets: e.sets.filter((s) => s.id !== setId) })))
  if (set.saved) {
    await enqueue({ method: 'DELETE', url: setUrl(workout, set) })
    flushSoon()
  }
}

export async function addExercise(exercise: ActiveExercise): Promise<void> {
  await updateActiveWorkout((w) => ({ ...w, exercises: [...w.exercises, exercise] }))
}

export async function removeExercise(exerciseKey: string): Promise<void> {
  const workout = getActiveWorkout()
  const exercise = workout?.exercises.find((e) => e.key === exerciseKey)
  if (!workout || !exercise) return
  await setActiveWorkout({ ...workout, exercises: workout.exercises.filter((e) => e.key !== exerciseKey) })
  for (const set of exercise.sets) {
    if (set.saved) await enqueue({ method: 'DELETE', url: setUrl(workout, set) })
  }
  flushSoon()
}

/**
 * Новый порядок упражнений: номера (exercise_position) идут по нему, уже отправленные подходы
 * упражнений с новым номером уходят на сервер ещё раз.
 */
async function saveOrder(workout: ActiveWorkout, order: ActiveExercise[], deleted: ActiveSet[] = []): Promise<void> {
  const exercises = renumberExercises(order)
  await setActiveWorkout({ ...workout, exercises })
  for (const set of deleted) await enqueue({ method: 'DELETE', url: setUrl(workout, set) })
  let sent = deleted.length > 0
  for (const [i, exercise] of exercises.entries()) {
    if (exercise === order[i]) continue
    for (const set of exercise.sets.filter((s) => s.saved)) {
      await putSet(workout, exercise, set)
      sent = true
    }
  }
  if (sent) flushSoon()
}

/** Сдвинуть упражнение на одно место вверх (-1) или вниз (+1). */
export async function moveExercise(exerciseKey: string, direction: -1 | 1): Promise<void> {
  const workout = getActiveWorkout()
  const from = workout?.exercises.findIndex((e) => e.key === exerciseKey) ?? -1
  const to = from + direction
  if (!workout || from < 0 || to < 0 || to >= workout.exercises.length) return
  const order = [...workout.exercises]
  ;[order[from], order[to]] = [order[to]!, order[from]!]
  await saveOrder(workout, order)
}

/**
 * Заменить упражнение на месте (`replacement` — собрано `buildExercise` по
 * `replacementTargets`). Выполненные подходы не теряются: они остаются у старого упражнения,
 * а новое встаёт сразу после него. Без выполненных — старое уходит вместе с подходами.
 */
export async function replaceExercise(exerciseKey: string, replacement: ActiveExercise): Promise<void> {
  const workout = getActiveWorkout()
  const index = workout?.exercises.findIndex((e) => e.key === exerciseKey) ?? -1
  const old = workout?.exercises[index]
  if (!workout || !old) return
  const done = old.sets.filter((s) => s.completed)
  const order = [...workout.exercises]
  if (done.length === 0) {
    order[index] = { ...replacement, position: old.position }
    await saveOrder(workout, order, old.sets.filter((s) => s.saved))
  } else {
    order.splice(index, 1, { ...old, sets: done }, { ...replacement, position: nextPosition(workout) })
    await saveOrder(workout, order, old.sets.filter((s) => s.saved && !s.completed))
  }
}

/**
 * Правки ИИ («Применить» в «С ИИ»): новые упражнения — с весом из прошлого раза (`last`);
 * убрать, заменить, поменять подходы — только у упражнений без выполненных подходов (проверяется
 * ещё раз: пока ИИ думал, подход могли отметить). Порядок на тренировке ИИ не меняет.
 * Возвращает ключи добавленных упражнений.
 */
export async function applyAiEdit(operations: EditOperation[], last: (id: string) => WorkoutSet[]): Promise<string[]> {
  const workout = getActiveWorkout()
  if (!workout) return []
  let exercises = [...workout.exercises]
  let position = nextPosition(workout)
  const deleted: ActiveSet[] = []
  const changed: { exercise: ActiveExercise; set: ActiveSet }[] = []
  const added: string[] = []
  const anchors = new Map<string, string>()

  const build = (op: EditOperation): ActiveExercise | null => {
    const ex = op.exercise
    if (!ex?.exercise_id) return null
    const exercise = buildExercise({
      exercise_id: ex.exercise_id,
      name: ex.exercise_name ?? ex.name,
      tracking: (ex.tracking ?? 'weight_reps') as Tracking,
      position: position++,
      targets: aiTargets(ex),
      last: last(ex.exercise_id),
      reason: ex.notes,
    })
    added.push(exercise.key)
    return exercise
  }
  const insert = (exercise: ActiveExercise, after: string | null) => {
    if (after === null) {
      exercises.push(exercise)
      return
    }
    const anchor = anchors.get(after) ?? (after === 'start' ? null : after)
    const index = anchor === null ? -1 : exercises.findIndex((e) => e.key === anchor)
    if (anchor !== null && index < 0) exercises.push(exercise)
    else exercises.splice(index + 1, 0, exercise)
    anchors.set(after, exercise.key)
  }

  for (const op of operations) {
    if (op.op === 'add') {
      const exercise = build(op)
      if (exercise) insert(exercise, op.after)
      continue
    }
    const index = exercises.findIndex((e) => e.key === op.key)
    const current = exercises[index]
    if (!current || current.sets.some((s) => s.completed) || op.op === 'move') continue
    if (op.op === 'remove') {
      deleted.push(...current.sets.filter((s) => s.saved))
      exercises = exercises.filter((_, i) => i !== index)
    } else if (op.op === 'replace') {
      const exercise = build(op)
      if (!exercise) continue
      deleted.push(...current.sets.filter((s) => s.saved))
      exercises[index] = exercise
    } else {
      const count = op.sets ?? current.sets.length
      deleted.push(...current.sets.slice(count).filter((s) => s.saved))
      let draft: ActiveExercise = { ...current, sets: current.sets.slice(0, count) }
      while (draft.sets.length < count) draft = { ...draft, sets: [...draft.sets, nextSet(draft)] }
      const sets = draft.sets.map((s) => ({
        ...s,
        reps: op.reps ?? s.reps,
        weight_kg: op.weight_kg ?? s.weight_kg,
        duration_s: op.duration_s ?? s.duration_s,
        distance_m: op.distance_m ?? s.distance_m,
        rest_s: op.rest_s ?? s.rest_s,
      }))
      const next = { ...current, sets }
      for (const set of sets) if (set.saved) changed.push({ exercise: next, set })
      exercises[index] = next
    }
  }

  await setActiveWorkout({ ...workout, exercises })
  for (const set of deleted) await enqueue({ method: 'DELETE', url: setUrl(workout, set) })
  for (const { exercise, set } of changed) await putSet(workout, exercise, set)
  if (deleted.length || changed.length) flushSoon()
  return added
}

/** Завершить: POST /finish в очередь (после всех подходов), тренировка с устройства убирается. */
export async function finishWorkout(now: Date = new Date()): Promise<string | null> {
  const workout = getActiveWorkout()
  if (!workout) return null
  const paused_s = pausedSeconds(workout, now.getTime())
  await enqueue({
    method: 'POST',
    url: `${sessionUrl(workout)}/finish`,
    body: paused_s > 0 ? { ended_at: now.toISOString(), paused_s } : { ended_at: now.toISOString() },
  })
  restTimer.stop()
  cancelSetTimer()
  await setActiveWorkout(null)
  flushSoon()
  return workout.id
}

/**
 * Бросить тренировку, не сохраняя: из очереди убираются её неотправленные запросы.
 * Если что-то уже ушло на сервер, вернётся true — тогда её надо удалить на сервере (нужна связь).
 */
export async function discardWorkout(): Promise<{ id: string; reachedServer: boolean } | null> {
  const workout = getActiveWorkout()
  if (!workout) return null
  const dropped = await dropMutations(sessionUrl(workout))
  const queuedSession = dropped.some((m) => m.method === 'PUT' && m.url === sessionUrl(workout))
  restTimer.stop()
  cancelSetTimer()
  await setActiveWorkout(null)
  return { id: workout.id, reachedServer: !queuedSession }
}
